<script lang="ts" setup>
/**
 * @name Dashboard.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @usuario - Versión inicial
 */

import { ref, computed, onMounted, watch } from 'vue'
import { BitacoraData } from '@shared/types/base'
import { formatDate } from '@/utils/dateFormatter'
import { useStore } from 'vuex'
import { apiService } from '@/services/apiService'
import { deleteField, doc, getDoc, updateDoc } from 'firebase/firestore'
import { dbRegistros, auth, database } from '@/firebase/config'
import { get, ref as dbRef } from 'firebase/database'
import { PLAN_OPTIONS, EPlan } from '@shared/types/enums'
import BaseModal from '@/components/BaseModal.vue'
import { convertFirestoreTimestamp, formatPrice, formatNumber } from '@/components/utils/functions'
/**
 * @description Props del componente
 */
const props = defineProps<{
	bitacoraData?: BitacoraData
}>()

const store = useStore()
const currentInstitution = computed(() => store.getters['global/currentInstitution'])
const loading = ref(false)
const error = ref<string | null>(null)
const pricePerDocument = ref(0)
const creditAmount = ref(0)
const creditosProduccion = ref(0)
const creditosPruebas = ref(0)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const successMessage = ref('')
const limiteDocumentosPostpago = ref(20)
/**
 * @description Referencia para los datos de bitácora
 */
const bitacora = ref<BitacoraData | null>(props.bitacoraData || null)
const tipoPlan = ref<EPlan | undefined>(undefined)

/**
 * @description Obtiene los datos de bitácora desde Firestore
 *
 * Con Custom Claims, las reglas de Firestore verifican automáticamente
 * si el usuario tiene permisos de administrador. Si el usuario no es administrador,
 * Firestore rechazará la petición con permission-denied.
 */
const obtenerBitacora = async (idInstitution: string): Promise<BitacoraData | null> => {
	try {
		loading.value = true
		error.value = null

		if (!idInstitution) {
			console.warn('No se proporcionó ID de institución')
			return null
		}

		// Verificar que el usuario esté autenticado
		const currentUser = auth.currentUser
		if (!currentUser) {
			error.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
			return null
		}

		// Refrescar el token para asegurar que tenemos los custom claims actualizados
		// Esto es importante cuando el admin cambia en Realtime Database
		// El true fuerza el refresco del token
		const token = await currentUser.getIdToken(true)

		// Diagnóstico: Verificar si el Custom Claim está presente en el token
		try {
			// Decodificar el token para verificar los custom claims
			const base64Url = token.split('.')[1]
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
					.join('')
			)
			const decodedToken = JSON.parse(jsonPayload)

			if (decodedToken.role?.tipoRole !== 'ADMINISTRADOR') {
				console.warn('No tienes permisos para acceder a la bitácora. Se requieren permisos de administrador.')
			}
		} catch (err) {
			console.error('Error al decodificar el token:', err)
		}

		// Acceder directamente a Firestore
		// Las reglas de Firestore verifican automáticamente el custom claim 'role'
		// Si el usuario no es administrador, Firestore rechazará con permission-denied
		const bitacoraRef = doc(dbRegistros, 'bitacora', idInstitution)
		const bitacoraSnap = await getDoc(bitacoraRef)

		if (!bitacoraSnap.exists()) {
			error.value = 'Bitácora no encontrada para esta institución'
			return null
		}

		const data = bitacoraSnap.data() as BitacoraData

		// Convertir el Timestamp de Firestore a timestamp de JavaScript
		if (data.modificationDate && typeof data.modificationDate === 'object') {
			data.modificationDate = convertFirestoreTimestamp(data.modificationDate)
		}

		return {
			...data,
			idInstitution: idInstitution
		}
	} catch (err: any) {
		console.error('Error al obtener bitácora:', err)

		if (err.code === 'permission-denied') {
			error.value = 'No tienes permisos para acceder a esta bitácora. Se requieren permisos de administrador. Verifica la consola para más detalles.'
		} else if (err.code === 'unauthenticated') {
			error.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
		} else {
			error.value = err.message || 'Error al cargar los datos de bitácora'
		}

		return null
	} finally {
		loading.value = false
	}
}

/**
 * @description Carga los datos de bitácora cuando cambia la institución
 */
const cargarBitacora = async () => {
	if (!currentInstitution.value) {
		bitacora.value = null
		return
	}

	const datos = await obtenerBitacora(currentInstitution.value)
	if (datos) {
		bitacora.value = datos
		tipoPlan.value = bitacora.value?.plan
	} else {
		bitacora.value = null
	}
}

// Cargar datos cuando el componente se monte
onMounted(() => {
	if (props.bitacoraData) {
		bitacora.value = props.bitacoraData
		tipoPlan.value = bitacora.value?.plan
	} else {
		cargarBitacora()
	}
})

// Observar cambios en la institución actual
watch(
	() => currentInstitution.value,
	() => {
		if (!props.bitacoraData) {
			cargarBitacora()
		}
	}
)

/**
 * @description Formatea la fecha de modificación
 */
const formattedModificationDate = computed(() => {
	if (!bitacora.value?.modificationDate) return 'No disponible'

	const timestamp = convertFirestoreTimestamp(bitacora.value.modificationDate)
	if (timestamp === 0) return 'No disponible'

	return formatDate(timestamp, 'YYYY-MM-DD HH:mm')
})

const cambiarPrecioPorDocumento = async () => {
	if (!pricePerDocument.value) {
		error.value = 'El precio por documento es requerido'
		return
	}

	try {
		// Obtener el usuario actual
		const currentUser = auth.currentUser
		if (!currentUser) {
			error.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
			return
		}

		const userSnapshot = await get(dbRef(database, `users/${currentUser.uid}`))
		const userData = userSnapshot.val()
		const userId = userData.curp || 'N/A'

		const docRef = doc(dbRegistros, 'bitacora', currentInstitution.value)
		await updateDoc(docRef, {
			pricePerDocument: pricePerDocument.value,
			LastUserWhomodified: userId
		})
			.then(() => {
				successMessage.value = 'Precio por documento actualizado correctamente'
				showSuccessModal.value = true
			})
			.catch(() => {
				error.value = 'Error al actualizar el precio por documento'
				showErrorModal.value = true
			})
			.finally(() => {
				cargarBitacora()
				pricePerDocument.value = 0
			})
	} catch (err: any) {
		console.error('Error al obtener usuario:', err)
		error.value = 'Error al obtener información del usuario'
		showErrorModal.value = true
	}
}

const cambiarPlaBitacora = async () => {
	const docRef = doc(dbRegistros, 'bitacora', currentInstitution.value)
	if (!tipoPlan.value) {
		error.value = 'Selecciona un plan antes de guardar'
		showErrorModal.value = true
		return
	}
	if (tipoPlan.value === EPlan.PREPAID && creditosProduccion.value === 0 && creditosPruebas.value === 0) {
		error.value = 'Para cambiar al plan prepago los campos de creditos deben tener un valor distinto de 0'
		showErrorModal.value = true
		return
	}
	if (tipoPlan.value === EPlan.POSTPAID && limiteDocumentosPostpago.value === 0) {
		error.value = 'Para cambiar al plan postpago el campo de límite de documentos debe tener un valor distinto de 0'
		showErrorModal.value = true
		return
	}
	const payload: Record<string, any> = {
		plan: tipoPlan.value,
		totalDocumentsProduction: creditosProduccion.value,
		totalDocumentsTest: creditosPruebas.value
	}

	// Firestore no soporta `undefined`. Si el plan NO es postpago, borramos el campo.
	if (tipoPlan.value === EPlan.POSTPAID) {
		payload.limitDocumentsPostpaid = limiteDocumentosPostpago.value
	} else {
		payload.limitDocumentsPostpaid = deleteField()
	}

	await updateDoc(docRef, payload)
		.then(async () => {
			successMessage.value = 'Plan actualizado correctamente'
			showSuccessModal.value = true
		})
		.catch(() => {
			error.value = 'Error al actualizar el plan'
			showErrorModal.value = true
		})
		.finally(() => {
			cargarBitacora()
			creditosProduccion.value = 0
			creditosPruebas.value = 0
		})
}

const aumentarCreditos = async (environment: 'production' | 'test') => {
	if (!creditAmount.value) {
		error.value = 'La cantidad de créditos es requerida'
		return
	}

	try {
		// Obtener el usuario actual
		const currentUser = auth.currentUser
		if (!currentUser) {
			error.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
			return
		}
		const userSnapshot = await get(dbRef(database, `users/${currentUser.uid}`))
		const userData = userSnapshot.val()

		// Llamar al servicio HTTP para crear la transacción
		const response = await apiService.post('/bitacora/transaccion', {
			idInstitution: currentInstitution.value,
			typeTransaction: 'aumento',
			quantityDocuments: creditAmount.value,
			environment: environment,
			details: `Aumento manual de créditos: ${creditAmount.value} documentos`,
			pricePerDocument: bitacora.value?.pricePerDocument || 0,
			packageId: '',
			userId: userData.curp || 'N/A'
		})

		if (response.code === 1) {
			// La transacción se creó correctamente y el balance ya fue actualizado
			successMessage.value = 'Créditos actualizados correctamente. Transacción registrada.'
			showSuccessModal.value = true
			cargarBitacora()
			creditAmount.value = 0
		} else {
			throw new Error(response.message || 'Error al crear la transacción')
		}
	} catch (err: any) {
		console.error('Error al aumentar créditos:', err)
		error.value = err.message || 'Error al actualizar los créditos'
		showErrorModal.value = true
		error.value = 'Error al actualizar los créditos: ' + (err.message || 'Error desconocido')
	}
}
</script>

<template>
	<!-- Estado de carga -->
	<div v-if="loading" class="loading-state">
		<div class="loader"></div>
		<p class="loading-text">Cargando datos de bitácora...</p>
	</div>

	<va-card v-if="bitacora" class="bitacora-card">
		<va-card-title class="header-gradient">
			<div class="header-content">
				<div class="header-main">
					<div class="header-icon-wrapper">
						<va-icon name="account_balance" class="header-icon" />
					</div>
					<div class="header-text">
						<h2 class="header-title">{{ bitacora.institutionName }}</h2>
						<p class="header-subtitle">ID: {{ bitacora.idInstitution }}</p>
						<p class="header-subtitle">Plan: {{ PLAN_OPTIONS.find(option => option.value === bitacora?.plan)?.text }}</p>
					</div>
				</div>
			</div>
		</va-card-title>

		<va-card-content class="card-content">
			<!-- Sección de Documentos -->
			<div class="section-title" v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC">
				<div class="section-title-content">
					<va-icon name="description" class="title-icon" />
					<span>Documentos</span>
				</div>
			</div>

			<div class="package-grid" v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC">
				<div class="package-card production-card">
					<div class="card-gradient production-gradient"></div>
					<div class="card-content-wrapper">
						<div class="card-header">
							<div class="package-avatar production-avatar">
								<va-icon name="check_circle" class="package-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Producción</h3>
								<p class="card-subtitle">Documentos en producción</p>
							</div>
						</div>
						<div class="card-value production-value">
							{{ formatNumber(bitacora.totalDocumentsProduction) }}
						</div>
					</div>
				</div>

				<div class="package-card test-card">
					<div class="card-gradient test-gradient"></div>
					<div class="card-content-wrapper">
						<div class="card-header">
							<div class="package-avatar test-avatar">
								<va-icon name="warning" class="package-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Pruebas</h3>
								<p class="card-subtitle">Documentos en modo test</p>
							</div>
						</div>
						<div class="card-value test-value">
							{{ formatNumber(bitacora.totalDocumentsTest) }}
						</div>
					</div>
				</div>
			</div>

			<!-- Sección de Información Financiera -->
			<div class="section-title section-spacing" v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC">
				<div class="section-title-content">
					<va-icon name="attach_money" class="title-icon" />
					<span>Información Financiera</span>
				</div>
			</div>

			<div class="package-card financial-card" v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC">
				<div class="card-content-wrapper">
					<div class="financial-header">
						<div class="financial-icon-wrapper">
							<va-icon name="attach_money" class="financial-icon" />
						</div>
						<div class="financial-content">
							<h3 class="card-title">Precio por Documento</h3>
							<p class="card-subtitle">Costo unitario configurado</p>
						</div>
					</div>
					<div class="financial-value">
						{{ formatPrice(bitacora.pricePerDocument) }}
					</div>
				</div>
			</div>

			<!-- Sección de Información de Modificación -->
			<div class="section-title section-spacing">
				<div class="section-title-content">
					<va-icon name="event" class="title-icon" />
					<span>Información de Modificación</span>
				</div>
			</div>

			<div class="package-grid last-section">
				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="event" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Última Modificación</h3>
								<p class="card-subtitle">Fecha y hora de actualización</p>
							</div>
						</div>
						<div class="info-value">
							{{ formattedModificationDate }}
						</div>
					</div>
				</div>

				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="person" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Último Usuario</h3>
								<p class="card-subtitle">Usuario que realizó la modificación</p>
							</div>
						</div>
						<div class="info-value">
							{{ bitacora.LastUserWhomodified || 'No disponible' }}
						</div>
					</div>
				</div>
			</div>
			<div v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC" class="section-title section-spacing">
				<div class="section-title-content">
					<va-icon name="attach_money" class="title-icon" />
					<span>Cambios de precio por documento</span>
				</div>
			</div>

			<div v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC" class="package-grid last-section">
				<div class="package-card production-card">
					<div class="card-gradient production-gradient"></div>
					<div class="card-content-wrapper">
						<div class="card-header">
							<div class="package-avatar production-avatar">
								<va-icon name="attach_money" class="package-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Esta sección permite cambiar el precio por documento en la bitácora</h3>
							</div>
						</div>
						<div class="card-value-price">
							<div class="price-input-container">
								<div class="input-wrapper">
									<va-icon name="attach_money" class="input-icon" />
									<va-input v-model="pricePerDocument" type="number" step="0.01" placeholder="0.00" class="price-input" :rules="[value => !!value || 'El precio por documento es requerido']" />
								</div>
								<va-button @click="cambiarPrecioPorDocumento" class="price-button" color="primary" :disabled="!pricePerDocument || pricePerDocument <= 0">
									<va-icon name="update" class="button-icon" />
									Actualizar precio
								</va-button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div v-if="bitacora?.plan === EPlan.PREPAID" class="section-title section-spacing">
				<div class="section-title-content">
					<va-icon name="description" class="title-icon" />
					<span>Aumentar créditos</span>
				</div>
			</div>

			<div v-if="bitacora?.plan === EPlan.PREPAID" class="package-grid last-section">
				<div class="package-card production-card">
					<div class="card-gradient credit-gradient"></div>
					<div class="card-content-wrapper">
						<div class="card-header">
							<div class="package-avatar credit-avatar">
								<va-icon name="description" class="package-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Esta sección permite agregar creditos a la bitacora den producción o pruebas</h3>
							</div>
						</div>
						<div class="card-value-price">
							<div class="credit-input-container">
								<div class="input-wrapper">
									<va-icon name="credit_card" class="credit-icon" />
									<va-input v-model="creditAmount" type="number" step="0.01" placeholder="0.00" class="price-input" :rules="[value => !!value || 'La cantidad de créditos es requerida']" />
								</div>
								<va-button @click="aumentarCreditos('production')" class="price-button" color="success" :disabled="!creditAmount || creditAmount <= 0">
									<va-icon name="add" class="button-icon" />
									Aumentar créditos producción
								</va-button>
								<va-button @click="aumentarCreditos('test')" class="price-button" color="success" :disabled="!creditAmount || creditAmount <= 0">
									<va-icon name="add" class="button-icon" />
									Aumentar créditos test
								</va-button>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="section-title section-spacing">
				<div class="section-title-content">
					<va-icon name="description" class="title-icon" />
					<span>Cambiar plan</span>
				</div>
			</div>

			<div class="package-grid last-section">
				<div class="package-card production-card">
					<div class="card-gradient production-gradient"></div>
					<div class="card-content-wrapper">
						<div class="card-header">
							<div class="package-avatar production-avatar">
								<va-icon name="description" class="package-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">En esta sección permite cambiar el tipo de plan a la institucón</h3>
							</div>
						</div>
						<div class="card-value-price">
							<div class="price-input-container plan-change-container">
								<div class="input-wrapper plan-change-wrapper">
									<div class="row">
										<div class="col-md-12" style="margin-bottom: 10px"><VaRadio v-model="tipoPlan" :options="PLAN_OPTIONS" value-by="value" /></div>
										<div class="col-md-12">
											<va-input
												v-if="tipoPlan === EPlan.PREPAID"
												v-model.number="creditosProduccion"
												:disabled="bitacora?.plan === tipoPlan"
												type="number"
												label="Créditos para Producción"
												placeholder="0"
												class="form-input"
												style="margin-right: 5px"
												:rules="[value => (typeof value === 'number' && value >= 0) || 'El valor no puede ser negativo']" />
											<va-input
												v-if="tipoPlan === EPlan.PREPAID"
												v-model.number="creditosPruebas"
												:disabled="bitacora?.plan === tipoPlan"
												type="number"
												label="Créditos para Pruebas"
												placeholder="0"
												class="form-input"
												:rules="[value => (typeof value === 'number' && value >= 0) || 'El valor no puede ser negativo']" />
										</div>
										<div class="col-md-12">
											<va-input
												v-if="tipoPlan === EPlan.POSTPAID"
												v-model.number="limiteDocumentosPostpago"
												:disabled="bitacora?.plan === tipoPlan"
												type="number"
												label="Límite de documentos para postpago"
												placeholder="0"
												class="form-input"
												style="margin-right: 5px"
												:rules="[value => (typeof value === 'number' && value >= 0) || 'El valor no puede ser negativo']" />
										</div>
									</div>
								</div>
								<va-button @click="cambiarPlaBitacora" class="price-button" color="primary" :disabled="bitacora?.plan === tipoPlan">
									<va-icon name="update" class="button-icon" />
									Cambiar tipo de plan
								</va-button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</va-card-content>
	</va-card>

	<!-- Estado vacío -->
	<div v-else-if="!loading && !bitacora" class="empty-state">
		<div class="empty-icon-wrapper">
			<va-icon name="error" class="empty-icon" size="8rem" />
		</div>
		<h3 class="empty-title">No hay datos de bitácora</h3>
		<p class="empty-description">No se encontraron datos de bitácora para mostrar</p>
	</div>
	<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al guardar los datos'" :test-mode="true" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ error || 'Error al guardar los datos' }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="" :message="successMessage || 'Datos guardados correctamente'" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage || 'Datos actualizados correctamente' }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.bitacora-card {
	border-radius: 20px;
	padding: 1rem;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	overflow: hidden;
	background: white;
	border: none;
	width: 100%;
	display: flex;
	flex-direction: column;
}

/* Header mejorado */
.header-gradient {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 0.75rem;
	border-radius: 0;
	position: relative;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.5rem;
}

.header-icon-wrapper {
	width: 64px;
	height: 64px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

.header-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.9);
	margin: 0.5rem 0 0 0;
	font-weight: 500;
}

.card-content {
	padding: 2.5rem;
	padding-bottom: 3rem;
	background: #fafbfc;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
}

/* Scrollbar personalizado para el contenido */
.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb:hover {
	background: #555;
}

.last-section {
	margin-bottom: 0;
}

/* Secciones */
.section-title {
	margin-bottom: 2rem;
	padding-bottom: 1rem;
	border-bottom: 3px solid transparent;
	border-image: linear-gradient(135deg, #062342 0%, #062342 100%) 1;
	animation: slideInLeft 0.5s ease-out;
}

.section-title-content {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a202c;
	letter-spacing: -0.5px;
}

.section-spacing {
	margin-top: 3rem;
}

.title-icon {
	color: #062342;
	font-size: 1.75rem;
	animation: pulse 2s ease-in-out infinite;
}

/* Grid de tarjetas */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1.5rem;
}

/* Tarjetas base */
.package-card {
	position: relative;
	background: white;
	border-radius: 16px;
	padding: 0;
	transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	animation: fadeInScale 0.6s ease-out;
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.package-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.5) 50%, transparent 100%);
	opacity: 0;
	transition: opacity 0.3s ease;
}

.package-card:hover {
	transform: translateY(-8px) scale(1.02);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.package-card:hover::before {
	opacity: 1;
}

/* Gradientes de tarjetas de documentos */
.card-gradient {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 120px;
	border-radius: 16px 16px 0 0;
	opacity: 0.15;
	transition: opacity 0.3s ease;
}

.production-gradient {
	background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
}

.credit-gradient {
	background: linear-gradient(135deg, #23e537 0%, #1e8a58 100%);
}
.test-gradient {
	background: linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%);
}

.package-card:hover .card-gradient {
	opacity: 0.25;
}

.card-content-wrapper {
	position: relative;
	padding: 1.75rem;
	z-index: 1;
}

.card-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.card-title-group {
	flex: 1;
}

.card-title {
	font-size: 1.125rem;
	font-weight: 700;
	color: #1a202c;
	margin: 0 0 0.25rem 0;
	letter-spacing: -0.3px;
}

.card-subtitle {
	font-size: 0.875rem;
	color: #718096;
	margin: 0;
	font-weight: 500;
}

/* Avatares mejorados */
.package-avatar {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	transition: all 0.3s ease;
	position: relative;
	overflow: hidden;
}

.package-avatar::before {
	content: '';
	position: absolute;
	top: -50%;
	left: -50%;
	width: 200%;
	height: 200%;
	background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
	transform: rotate(45deg);
	transition: all 0.5s ease;
}

.package-card:hover .package-avatar::before {
	animation: shine 0.6s ease;
}

.production-avatar {
	background: linear-gradient(90deg, #062342 0%, #062342 100%);
}

.credit-avatar {
	background: linear-gradient(90deg, #23e537 0%, #1e8a58 100%);
}

.test-avatar {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}

.package-icon {
	color: white;
	font-size: 1.5rem;
	position: relative;
	z-index: 1;
}

/* Valores de las tarjetas */
.card-value {
	font-size: 2.5rem;
	font-weight: 800;
	letter-spacing: -1px;
	margin-top: 0.5rem;
	transition: all 0.3s ease;
}

.card-value-price {
	margin-top: 4rem;
	transition: all 0.3s ease;
}

.price-input-container {
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	padding: 1.5rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	border-radius: 16px;
	border: 2px solid rgba(37, 99, 235, 0.1);
	transition: all 0.3s ease;
}

.package-card:hover .price-input-container {
	border-color: rgba(37, 99, 235, 0.3);
	box-shadow: 0 4px 20px rgba(37, 99, 235, 0.1);
	transform: translateY(-2px);
}

.credit-input-container {
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	padding: 1.5rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	border-radius: 16px;
	border: 2px solid rgba(44, 235, 37, 0.1);
	transition: all 0.3s ease;
}

.package-card:hover .credit-input-container {
	border-color: rgba(111, 188, 172, 0.3);
	box-shadow: 0 4px 20px rgba(44, 235, 37, 0.1);
	transform: translateY(-2px);
}

.input-wrapper {
	position: relative;
	display: flex;
	align-items: center;
	gap: 0.75rem;
	min-width: 0;
}

.input-icon {
	color: #062342;
	font-size: 1.5rem;
	flex-shrink: 0;
	opacity: 0.7;
	transition: all 0.3s ease;
}
.credit-icon {
	color: #23e537;
	font-size: 1.5rem;
	flex-shrink: 0;
	opacity: 0.7;
	transition: all 0.3s ease;
}
.credit-input-container:hover .credit-icon {
	opacity: 1;
	transform: scale(1.1);
}

.price-input-container:hover .input-icon {
	opacity: 1;
	transform: scale(1.1);
}

.credit-input-container:hover .input-icon {
	opacity: 1;
	transform: scale(1.1);
}

.price-input {
	flex: 1;
}

.price-input :deep(.va-input-wrapper) {
	background: white;
	border: 2px solid #e5e7eb;
	border-radius: 12px;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.price-input :deep(.va-input-wrapper:hover) {
	border-color: #062342;
	box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
}

.price-input :deep(.va-input-wrapper:focus-within) {
	border-color: #062342;
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.price-input :deep(input) {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a202c;
	padding: 0.875rem 1rem;
}

.price-button {
	width: 100%;
	padding: 0.875rem 1.5rem;
	font-size: 1rem;
	font-weight: 600;
	border-radius: 12px;
	box-shadow: 0 4px 12px rgba(062342, 0.3);
	transition: all 0.3s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	text-transform: none;
	letter-spacing: 0.3px;
}

.price-button:hover:not(:disabled) {
	transform: translateY(-2px);
	box-shadow: 0 6px 20px rgba(062342, 0.4);
}

.price-button:active:not(:disabled) {
	transform: translateY(0);
}

.price-button:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.button-icon {
	font-size: 1.125rem;
}

@media (min-width: 768px) {
	.price-input-container {
		flex-direction: row;
		flex-wrap: wrap;
		align-items: flex-end;
	}

	.credit-input-container {
		flex-direction: row;
		flex-wrap: wrap;
		align-items: flex-end;
	}

	.input-wrapper {
		flex: 1;
	}

	.price-button {
		width: auto;
		min-width: 200px;
		max-width: 100%;
		white-space: nowrap;
	}
}

/* Ajustes específicos: sección "Cambiar plan" (evitar overflow al redimensionar) */
.plan-change-wrapper {
	display: block;
	width: 100%;
}

.plan-change-wrapper .row {
	margin-left: 0;
	margin-right: 0;
}

.plan-change-wrapper [class^='col-'],
.plan-change-wrapper [class*=' col-'] {
	padding-left: 0;
	padding-right: 0;
}

@media (min-width: 768px) {
	.plan-change-container {
		align-items: flex-end;
	}

	.plan-change-container .price-button {
		margin-left: auto;
	}
}

.production-value {
	color: #667eea;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.test-value {
	color: #f5576c;
	background: linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.package-card:hover .card-value {
	transform: scale(1.05);
}

/* Tarjeta financiera */
.financial-card {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	color: white;
}

.financial-card .card-content-wrapper {
	padding: 2rem;
}

.financial-header {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	margin-bottom: 1.5rem;
}

.financial-icon-wrapper {
	width: 64px;
	height: 64px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.financial-icon {
	color: white;
	font-size: 2rem;
}

.financial-content .card-title {
	color: white;
}

.financial-content .card-subtitle {
	color: rgba(255, 255, 255, 0.8);
}

.financial-value {
	font-size: 3rem;
	font-weight: 800;
	color: white;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -1px;
}

/* Tarjetas de información */
.info-card {
	background: white;
}

.info-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.info-icon-wrapper {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease;
}

.package-card:hover .info-icon-wrapper {
	background: linear-gradient(135deg, #667eea30 0%, #764ba230 100%);
	transform: scale(1.1);
}

.info-icon {
	color: #667eea;
	font-size: 1.5rem;
}

.info-value {
	font-size: 1.25rem;
	font-weight: 700;
	color: #1a202c;
	letter-spacing: -0.3px;
}

/* Estado de carga */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: white;
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
}

.loader {
	border: 4px solid #f3f4f6;
	border-radius: 50%;
	border-top: 4px solid #062342;
	width: 60px;
	height: 60px;
	animation: spin 1s linear infinite;
	box-shadow: 0 4px 12px rgba(062342, 0.2);
	margin-bottom: 1.5rem;
}

.loading-text {
	margin-top: 1rem;
	color: #6c757d;
	font-size: 1rem;
	font-weight: 500;
}

/* Estado vacío mejorado */
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: white;
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
}

.empty-icon-wrapper {
	width: 120px;
	height: 120px;
	border-radius: 50%;
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 2rem;
	animation: float 3s ease-in-out infinite;
}

.empty-icon {
	padding: 0.75rem;
	color: #dc2626;
}

.empty-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: #1a202c;
	margin-bottom: 0.75rem;
	letter-spacing: -0.5px;
}

.empty-description {
	color: #718096;
	font-size: 1.125rem;
	font-weight: 500;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Responsive */
@media (max-width: 1024px) {
	.package-grid {
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 1.25rem;
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.package-grid {
		grid-template-columns: 1fr;
		gap: 1rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	.section-title-content {
		font-size: 1.25rem;
	}

	.card-value {
		font-size: 2rem;
	}

	.financial-value {
		font-size: 2.25rem;
	}

	.section-spacing {
		margin-top: 2rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.card-content-wrapper {
		padding: 1.25rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}

	.card-value {
		font-size: 1.75rem;
	}

	.financial-value {
		font-size: 2rem;
	}
}
</style>
